import { TemplateField } from './../activities/print-template-activity';
import { Utilities } from 'src/app/services';
import { DataEntity } from './data-entity';
import { DataEntityListItem } from '../../components/workflow/activities/print-template-activity/print-template-activity-editor/print-template-activity-editor.component';
import { fabric } from 'fabric';
import { Observable, of } from 'rxjs';

export class DataSourceCodes {
  static INSPECTOR_LIST_CODE = 'inspector-list';
  static SIMPLE_INSPECTOR_LIST_CODE = 'inspector-simple-list';
  static CONSOLIDATED_INSPECTOR_LIST_CODE = 'consolidated-inspector-list';
  static STATE_LIST_CODE = 'state-list';
  static SIGN_OFF_ASSIGNED_LIST_CODE = 'sign-off-user-list';

  static DYNAMIC_ID_LIST_CODES = [
    DataSourceCodes.INSPECTOR_LIST_CODE,
    DataSourceCodes.SIMPLE_INSPECTOR_LIST_CODE,
    DataSourceCodes.CONSOLIDATED_INSPECTOR_LIST_CODE,
    DataSourceCodes.STATE_LIST_CODE,
    DataSourceCodes.SIGN_OFF_ASSIGNED_LIST_CODE
  ];
}

export enum ControlType {
  Radio = 'radio',
  Check = 'checkbox',
  Dropdown = 'dropdown',
  MultiSelect = 'multi-select'
}

export class ListOption {
  constructor(options: Partial<ListOption>) {
    Object.assign(this, options);
  }
  id: string = Utilities.generateId();
  customListId: string;
  position: number;
  value: string;
  text: string;
  imagePath?: string;
}

export class ListDataEntity extends DataEntity {
  controlType: ControlType = ControlType.Radio;
  listTitle = '';
  listValues = [];
  listTextValues: ListOption[] = [];
  selectedDataSource: string;
  availableDataSources: { [key: string]: string };
  excludeItemsFromCriteria: string[];
  availableListItems: ListOption[] = [];

  selectedValue = 'fake';
  selectedTextValues = [];
  selectedValues = [];
  selectedItems: ListOption[];

  canEditList: boolean;

  constructor(options?: Partial<ListDataEntity>) {
    super(options);

    this.dataEntityTypeCode = 'list-data';
    this.dataEntityTypeDescription = 'Use this entity type to create a list.';
    this.dataEntityTypeName = 'List';

    if (options) {
      Object.assign(this, options);
      // support legecy listValues[]:string[]
      if (
        this.listValues &&
        this.listValues.length > 0 &&
        !this.listTextValues
      ) {
        this.listTextValues = this.listValues.map(v => {
          return new ListOption({ text: v, value: v });
        });
      }

      // change the this.value to set the selectedTextValues... this is to support legacy.
      let isLegacy = false;

      if (this.value) {
        if ((this.selectedValues || []).length === 0
          && (this.value || '') !== ''
        ) {
          if ((this.value || '').indexOf('[') === -1) {
            isLegacy = true;

            const items = this.value.split(',');

            this.selectedValues = items.map(m => {
              return {
                id: '',
                text: m,
                value: m
              };
            });
          }
        }

        // const textValueArray = this.selectedValues;

        let textValueArray: any[] = [];

        if (Array.isArray(this.selectedValues)) {
          textValueArray = this.selectedValues;
        } else if (this.selectedValues) {
          // handle object or single value
          textValueArray = [this.selectedValues];
        }

        // if this is not GeoPermits legacy list value, then it
        // could still be a migrated value, which is probably missing ids
        // inspector lists also get their IDs generated dynamically each time
        // the available items are loaded, so we can't match the selected value's
        // ID to an available item
        const compareByValue = textValueArray.some(tv => !tv.id)
          || DataSourceCodes.DYNAMIC_ID_LIST_CODES.includes(this.selectedDataSource);

        const operation = (list1, list2) =>
          list1.filter(
            a => (
              isLegacy
              && list2.some(b => a.value === b.value || a.id === b.value)
            )
              || (
                !isLegacy
                && !compareByValue
                && list2.some(b => a.id === b.id)
              )
              || (
                !isLegacy
                && compareByValue
                && list2.some(b => a.value === b.value)
              )
          );

        const inBoth = (list1, list2) => operation(list1, list2);

        if (this.isEditMode) {
          const newValuesArray = inBoth(this.availableListItems, textValueArray);

          this.selectedTextValues = newValuesArray;
        }
        else {
          this.selectedTextValues = this.selectedValues;
        }
      }
    }

    // convert options into ListOption classes
    if (this.listTextValues) {
      this.listTextValues = this.listTextValues.map(l => new ListOption(l));
    }
  }

  override getFormulaValue(): Observable<any> {
    let parsedValue;

    try {
      parsedValue = JSON.parse(this.value);
    } catch (e) {

      parsedValue = this.value;
    }


    if (parsedValue !== undefined && parsedValue !== null) {
      this.selectedValues = parsedValue;
    }

    // Handle multi-select controls (both MultiSelect and Check/checkbox which allow multiple selections)
    if(this.controlType === ControlType.MultiSelect || this.controlType === ControlType.Check) {
      return of((this.selectedValues || []).map(i => i.value).join(','));
    }
    else {
      return of((this.selectedValues || [])[0]?.value || '');
    }
  }

  getPrintTemplateItems(): DataEntityListItem[] {

    const hasImages: boolean = (this.controlType == ControlType.Radio || this.controlType == ControlType.Dropdown) && this.availableListItems && this.availableListItems.some(i => i.imagePath);

    let listItems: DataEntityListItem[] = [
      new DataEntityListItem({
        code: this.templateCode,
        text: 'Selected Value',
        extraData: 'Selected'
      })
    ];

    if (hasImages) {
      listItems.push(
        new DataEntityListItem({
          code: this.templateCode,
          text: 'Selected Image',
          extraData: 'SelectedImage'
        })
      );
    }

    return [
      new DataEntityListItem({
        code: this.templateCode,
        text: this.templateCode,
        children: [
          ...this.availableListItems.map(
            a =>
              new DataEntityListItem({
                code: this.templateCode,
                text: a.text,
                extraData: a
              })
          ),
          ...listItems
        ]
      })
    ];
  }

  getSpecialAvailablePrintFormatOptions(): { [key: string]: string[] } {
    const listEntityAvailablePrintFormatOptions: {
      [key: string]: string[];
    } = {};

    const optionsForAnyListEntity = [
      'fontColor',
      'fontSize',
      'fontWeight',
      'emptyText',
      'orientation',
      'verticalOrientation'
    ];

    listEntityAvailablePrintFormatOptions[this.templateCode.toUpperCase() + '.SELECTED'] = [
      ...optionsForAnyListEntity,
      'criteria',
      'outputFormat'
    ];

    listEntityAvailablePrintFormatOptions[this.templateCode.toUpperCase() + '.SELECTEDIMAGE'] = [];

    for (const li of this.availableListItems) {
      listEntityAvailablePrintFormatOptions[
        (this.templateCode + '.' + li.text).toUpperCase()
      ] = [...optionsForAnyListEntity];
    }

    return listEntityAvailablePrintFormatOptions;
  }

  getPossibleValues() {
    const excludedItems = this.excludeItemsFromCriteria || [];
    return this.availableListItems
      .filter(f => excludedItems.indexOf(f.value) === -1)
      .map(l => {
        return { name: l.text || l.value, value: l.value || l.text };
      });
  }

  async formatTemplateItem(fieldInfo, helperFunctions): Promise<fabric.Object> {
    const obj: fabric.Object = await super.formatTemplateItem(
      fieldInfo,
      helperFunctions
    );

    obj.lockScalingY = false;
    obj.lockSkewingY = false;

    return of(obj).toPromise();
  }
}
